// src/features/tox/pages/TalkToxListPage.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageShell } from "@/ui/PageShell";
import { withBase } from "@/ui/Header";
import { usePageScale } from "@/ui/usePageScale";
import { WeeklyTox, weeklyToxList } from "@/lib/api";
import { goNativeHome } from "@/lib/nativeBridge";
import { registerPager, unregisterPager } from "@/lib/pagerBridge";
import { COLOR, FW } from "@/ui/tokens";

/* ──────────────── Layout spec (1264 x 1424 기준) ─────────────── */
const BASE = {
    W: 1264,

    // 탭 영역
    TAB_H: 112,
    TAB_FZ: 35,

    // 리스트 영역
    INNER_W: 1122, // 제목+이미지 전체 폭
    MARGIN_X: 71, // (1264 - 1122) / 2
    IMAGE_W: 268,
    IMAGE_H: 214,
    ROW_PADDING_Y: 24,
    LIST_TOP_GAP: 40,

    // 페이징
    PAGER_H: 20,
} as const;

const ROWS_PER_PAGE = 4;
const ROW_H = BASE.IMAGE_H + BASE.ROW_PADDING_Y * 2;

const ICONS = {
    first: withBase("icons/prev2.svg"),
    prev: withBase("icons/prev1.svg"),
    next: withBase("icons/next1.svg"),
    last: withBase("icons/next2.svg"),
    back: withBase("icons/prev1.svg"),
};

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");

function useScale() {
    const { s, px, sf, sfpx } = usePageScale({ baseW: BASE.W, max: 1 });
    return { s, px, sf, sfpx };
}

/* ──────────────── 이미지 URL 보정 (localhost → API_BASE) ─────────────── */
function resolveImageUrl(raw?: string | null): string | undefined {
    if (!raw) return undefined;
    if (raw.startsWith("/") && API_BASE) return `${API_BASE}${raw}`;
    if (raw.startsWith("http://localhost") || raw.startsWith("https://localhost")) {
        if (!API_BASE) return raw;
        try {
            const u = new URL(raw);
            const base = new URL(API_BASE);
            u.protocol = base.protocol;
            u.hostname = base.hostname;
            u.port = base.port;
            return u.toString();
        } catch {
            return raw;
        }
    }
    return raw;
}

/* ──────────────── Tabs (전체 / 일상 / 지식 / 건강) ─────────────── */

type TabValue = "ALL" | "DAILY" | "KNOW" | "HEALTH";

function Tabs({
                  value,
                  onChange,
                  s,
                  px,
                  sf,
                  sfpx,
              }: {
    value: TabValue;
    onChange: (v: TabValue) => void;
    s: (n: number) => number;
    px: (n: number) => string;
    sf: (n: number) => number;
    sfpx: (n: number) => string;
}) {
    const tabs: { value: TabValue; label: string }[] = [
        { value: "ALL", label: "전체" },
        { value: "DAILY", label: "일상" },
        { value: "KNOW", label: "지식" },
        { value: "HEALTH", label: "건강" },
    ];

    const Tab = ({ v, label }: { v: TabValue; label: string }) => {
        const active = value === v;
        return (
            <button
                onClick={() => onChange(v)}
                style={{
                    width: "100%",
                    height: px(BASE.TAB_H),
                    fontSize: sfpx(BASE.TAB_FZ),
                    fontWeight: FW.extraBold,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: COLOR.text,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderBottom: active
                        ? `${s(4)}px solid #000`
                        : `${s(2)}px solid "#e5e7eb"`,
                }}
            >
                {label}
            </button>
        );
    };

    return (
        <div
            style={{
                width: px(BASE.W),
                margin: "0 auto",
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
            }}
        >
            {tabs.map((t) => (
                <Tab key={t.value} v={t.value} label={t.label} />
            ))}
        </div>
    );
}

/* ──────────────── Pager ─────────────── */

function Pager({
                   hasResults,
                   page,
                   totalPages,
                   hasNext,
                   onFirst,
                   onPrev,
                   onNext,
                   onLast,
                   px,
                   sfpx,
               }: {
    hasResults: boolean;
    page: number;
    totalPages: number;
    hasNext: boolean;
    onFirst: () => void;
    onPrev: () => void;
    onNext: () => void;
    onLast: () => void;
    px: (n: number) => string;
    sfpx: (n: number) => string;
}) {
    if (!hasResults) {
        return <div style={{ height: px(BASE.PAGER_H) }} />;
    }

    return (
        <div style={{ width: "100%", height: px(BASE.PAGER_H) }}>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                }}
            >
                <button
                    disabled={!(page > 1)}
                    onClick={onFirst}
                    aria-label="처음"
                    style={{
                        lineHeight: 0,
                        background: "transparent",
                        marginRight: px(48),
                        border: "none",
                        opacity: page > 1 ? 1 : 0.4,
                        cursor: "pointer",
                    }}
                >
                    <img
                        src={ICONS.first}
                        alt=""
                        style={{ width: px(48), height: px(48), display: "block" }}
                    />
                </button>
                <button
                    disabled={!(page > 1)}
                    onClick={onPrev}
                    aria-label="이전"
                    style={{
                        lineHeight: 0,
                        background: "transparent",
                        marginRight: px(46),
                        border: "none",
                        opacity: page > 1 ? 1 : 0.4,
                        cursor: "pointer",
                    }}
                >
                    <img
                        src={ICONS.prev}
                        alt=""
                        style={{ width: px(48), height: px(48), display: "block" }}
                    />
                </button>

                <span
                    style={{
                        fontWeight: 700,
                        fontFeatureSettings: "'tnum' 1",
                        fontSize: sfpx(34),
                        minWidth: px(120),
                        textAlign: "center",
                        marginRight: px(46),
                    }}
                >
          {Math.max(1, page)} / {Math.max(1, totalPages)}
        </span>

                <button
                    disabled={!hasNext}
                    onClick={onNext}
                    aria-label="다음"
                    style={{
                        lineHeight: 0,
                        background: "transparent",
                        marginRight: px(48),
                        border: "none",
                        opacity: hasNext ? 1 : 0.4,
                        cursor: "pointer",
                    }}
                >
                    <img
                        src={ICONS.next}
                        alt=""
                        style={{ width: px(48), height: px(48), display: "block" }}
                    />
                </button>
                <button
                    disabled={!hasNext}
                    onClick={onLast}
                    aria-label="마지막"
                    style={{
                        lineHeight: 0,
                        background: "transparent",
                        border: "none",
                        opacity: hasNext ? 1 : 0.4,
                        cursor: "pointer",
                    }}
                >
                    <img
                        src={ICONS.last}
                        alt=""
                        style={{ width: px(48), height: px(48), display: "block" }}
                    />
                </button>
            </div>
        </div>
    );
}

/** 반응 합산 도우미 (likes_count 대신 사용) */
function getReactionTotal(tox: WeeklyTox | any): number {
    // 백엔드에서 미리 합계를 내려주는 경우
    if (typeof (tox as any).reaction_total === "number") {
        return (tox as any).reaction_total;
    }
    const r = ((tox as any).reactions || {}) as {
        like?: number;
        funny?: number;
        wow?: number;
        angry?: number;
        sad?: number;
    };
    return (
        (r.like ?? 0) +
        (r.funny ?? 0) +
        (r.wow ?? 0) +
        (r.angry ?? 0) +
        (r.sad ?? 0)
    );
}

/* ──────────────── Page ─────────────── */

export default function TalkToxListPage() {
    const nav = useNavigate();
    const { s, px, sf, sfpx } = useScale();

    const [items, setItems] = useState<WeeklyTox[]>([]);
    const [page, setPage] = useState(1);
    const size = 4; // 4개 고정
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const [tab, setTab] = useState<TabValue>("ALL"); // 지금은 UI만, 필터는 추후 연동

    const totalPages = Math.max(1, Math.ceil(total / size));
    const hasNext = page < totalPages;
    const hasResults = items.length > 0;

    // LibraryPage와 동일하게 4행 고정 높이
    const listHeight = s(ROW_H * ROWS_PER_PAGE);

    useEffect(() => {
        let ignore = false;
        (async () => {
            try {
                setLoading(true);
                const res = await weeklyToxList({ page, size });
                if (ignore) return;
                setItems(res.items || []);
                setTotal(res.total || 0);
            } catch (e) {
                if (!ignore) console.error(e);
            } finally {
                if (!ignore) setLoading(false);
            }
        })();
        return () => {
            ignore = true;
        };
    }, [page]);

    // 물리키 페이저 브릿지 등록
    useEffect(() => {
        registerPager({
            next: () => setPage((p) => Math.min(totalPages, p + 1)),
            prev: () => setPage((p) => Math.max(1, p - 1)),
            first: () => setPage(1),
            last: () => setPage(totalPages || 1),
        });
        return () => unregisterPager();
    }, [totalPages]);

    const handleBack = () => {
        const ok = goNativeHome();
        if (!ok) {
            nav(-1);
        }
    };

    return (
        <PageShell title="INNO STORY" width={px(BASE.W)} onBack={handleBack} backIcon={ICONS.back}>
            {/* LibraryPage와 같은 패턴: body 안에서 탭 → 리스트 → 페이징 */}
            <div style={{ gridArea: "body" }}>
                {/* 탭 */}
                {/*<Tabs value={tab} onChange={setTab} s={s} px={px} />*/}

                {/* 리스트 영역 (4칸 기준 고정 높이) */}
                <div style={{ width: px(BASE.W), margin: "0 auto" }}>
                    <div
                        style={{
                            marginLeft: px(BASE.MARGIN_X),
                            marginRight: px(BASE.MARGIN_X),
                            paddingTop: px(BASE.LIST_TOP_GAP),
                            height: `${listHeight}px`,
                            overflow: "hidden",
                            boxSizing: "border-box",
                        }}
                    >
                        {items.map((tox) => {
                            const imgSrc = resolveImageUrl(tox.image_url);
                            const reactionTotal = getReactionTotal(tox);

                            return (
                                <button
                                    key={tox.id}
                                    onClick={() =>
                                        nav(`/tox/${tox.id}`, {
                                            state: { fromPage: page, totalPages },
                                        })
                                    }
                                    style={{
                                        width: "100%",
                                        padding: `${px(BASE.ROW_PADDING_Y)} 0`,
                                        border: "none",
                                        borderBottom: "1px solid #e5e5e5",
                                        background: "transparent",
                                        cursor: "pointer",
                                        display: "grid",
                                        gridTemplateColumns: `${px(BASE.IMAGE_W)} 1fr`,
                                        columnGap: px(32),
                                        alignItems: "center",
                                        textAlign: "left",
                                    }}
                                >
                                    {/* 이미지 */}
                                    <div
                                        style={{
                                            width: px(BASE.IMAGE_W),
                                            height: px(BASE.IMAGE_H),
                                            overflow: "hidden",
                                            borderRadius: s(10),
                                            border: "1px solid #ccc",
                                            background: "#f4f4f4",
                                        }}
                                    >
                                        {imgSrc && (
                                            <img
                                                src={imgSrc}
                                                alt={tox.title}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                    display: "block",
                                                }}
                                            />
                                        )}
                                    </div>

                                    {/* 텍스트 영역 */}
                                    <div
                                        style={{
                                            height: px(BASE.IMAGE_H),
                                            paddingTop: px(35),
                                            paddingBottom: px(25),
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        {/* 제목 */}
                                        <div
                                            style={{
                                                fontSize: sf(36),
                                                fontWeight: FW.bold,
                                                lineHeight: 1.4,
                                                color: COLOR.text,
                                            }}
                                        >
                                            {tox.title}
                                        </div>

                                        {/* 공감 / 조회수 / 댓글 */}
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: px(16),
                                                fontSize: sf(18),
                                                alignItems: "center",
                                            }}
                                        >
                                              <span>
                                                  공감{" "}
                                                  {(tox.reactions_total ??
                                                      ((tox.reaction_like || 0) +
                                                          (tox.reaction_funny || 0) +
                                                          (tox.reaction_wow || 0) +
                                                          (tox.reaction_angry || 0) +
                                                          (tox.reaction_sad || 0)))}
                                              </span>
                                            <span>조회수 {tox.views_count ?? 0}</span>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}

                        {!hasResults && !loading && (
                            <div
                                style={{
                                    paddingTop: px(20),
                                    fontSize: sf(18),
                                    color: "#666",
                                    textAlign: "center",
                                }}
                            >
                                등록된 글이 없습니다.
                            </div>
                        )}
                    </div>
                </div>

                {/* 페이징 */}
                <div
                    style={{
                        marginLeft: px(BASE.MARGIN_X),
                        marginRight: px(BASE.MARGIN_X),
                        marginTop: px(156),
                    }}
                >
                    <Pager
                        hasResults={hasResults}
                        page={page}
                        totalPages={totalPages}
                        hasNext={hasNext}
                        onFirst={() => setPage(1)}
                        onPrev={() => setPage((p) => Math.max(1, p - 1))}
                        onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
                        onLast={() => setPage(totalPages || 1)}
                        px={px}
                        sfpx={sfpx}
                    />
                </div>
            </div>
        </PageShell>
    );
}
